import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '../../hooks/useQuery';
import { PokemonCard } from '../../types/pokemon';
import { Loader2, Package, LayersIcon, BarChart3, Plus, Minus, CheckCircle2, XCircle, Heart, Repeat, Sparkles, Zap, Info, Percent } from 'lucide-react';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useCollection } from '../../hooks/useCollection';
import { useSimilarCards } from '../../hooks/useSimilarCards';
import { supabase } from '../../lib/supabase';
import styled from 'styled-components';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTradeSettings } from '../../hooks/useTradeSettings';
import { CardDetailSkeleton } from '../../components/skeletons/CardDetailSkeleton';
import { Helmet } from 'react-helmet-async';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';

// Déclaration pour styled-components
declare module 'styled-components';

interface Trade {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  creator: {
    username: string;
  };
  wanted_card: {
    id: number;
    card_fr: string;
    card_en?: string;
    image: string;
    set: string;
    rarity: string;
  };
}

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
  img: string;
}

interface PackInfo {
  name: string;
  display_name: string;
  img: string;
}

const TradeCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
  }
`;

const TradeCardImage = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  background: linear-gradient(to bottom, #f3f4f6, #e5e7eb);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 1rem;
    transition: filter 0.2s;
    
    &.not-collected {
      filter: grayscale(100%);
    }
  }
`;

const TradeCardContent = styled.div`
  padding: 1.5rem;
`;

const TradeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Nouvelles probabilités par position selon spec_stat.md
const POSITION_PROBABILITIES_WITH_SHINY: Record<string, Record<string, number>> = {
  // Positions 1-3 (premières cartes)
  'first_three': {
    '♢': 1.0,    // 100%
    '♢♢': 0.0,   // 0%
    '♢♢♢': 0.0,  // 0%
    '♢♢♢♢': 0.0, // 0%
    '★': 0.0,    // 0%
    '★★': 0.0,   // 0%
    '★★★': 0.0,  // 0%
    '✧': 0.0,    // 0%
    '✧✧': 0.0,   // 0%
    '♛': 0.0,    // 0%
  },
  // Position 4
  'fourth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.89,    // 89%
    '♢♢♢': 0.05,   // 5%
    '♢♢♢♢': 0.0167, // 1.67%
    '★': 0.0257,   // 2.57%
    '★★': 0.005,   // 0.5%
    '★★★': 0.0022, // 0.22%
    '✧': 0.00714,  // 0.714%
    '✧✧': 0.00333, // 0.333%
    '♛': 0.0004,   // 0.04%
  },
  // Position 5
  'fifth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.56,    // 56%
    '♢♢♢': 0.19810,// 19.810%
    '♢♢♢♢': 0.0664,// 6.66%
    '★': 0.1029,   // 10.29%
    '★★': 0.02,    // 2%
    '★★★': 0.0089, // 0.89%
    '✧': 0.02857,  // 2.857%
    '✧✧': 0.01333, // 1.333%
    '♛': 0.0016,   // 0.16%
  }
};

// Anciennes probabilités pour les packs sans shiny
const POSITION_PROBABILITIES_NO_SHINY: Record<string, Record<string, number>> = {
  // Positions 1-3 (premières cartes)
  'first_three': {
    '♢': 1.0,    // 100%
    '♢♢': 0.0,   // 0%
    '♢♢♢': 0.0,  // 0%
    '♢♢♢♢': 0.0, // 0%
    '★': 0.0,    // 0%
    '★★': 0.0,   // 0%
    '★★★': 0.0,  // 0%
    '♛': 0.0,    // 0%
  },
  // Position 4
  'fourth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.9,     // 90%
    '♢♢♢': 0.05,   // 5%
    '♢♢♢♢': 0.0167, // 1.67%
    '★': 0.0257,   // 2.57%
    '★★': 0.005,   // 0.5%
    '★★★': 0.0022, // 0.22%
    '♛': 0.0004,   // 0.04%
  },
  // Position 5
  'fifth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.6,     // 60%
    '♢♢♢': 0.2,    // 20%
    '♢♢♢♢': 0.0666, // 6.66%
    '★': 0.1029,   // 10.29%
    '★★': 0.02,    // 2%
    '★★★': 0.0089, // 0.89%
    '♛': 0.0016,   // 0.16%
  }
};

// Fonction pour vérifier si un pack contient des cartes shiny
const hasShinyCards = (cards: PokemonCard[]): boolean => {
  return cards.some(card => card.rarity === '✧' || card.rarity === '✧✧');
};

// Fonction pour obtenir les probabilités appropriées
const getPositionProbabilities = (cards: PokemonCard[]): Record<string, Record<string, number>> => {
  return hasShinyCards(cards) ? POSITION_PROBABILITIES_WITH_SHINY : POSITION_PROBABILITIES_NO_SHINY;
};

// Fonction pour formater un pourcentage
const formatPercent = (value: number) => {
  // S'assurer que la valeur est entre 0 et 1
  const clampedValue = Math.max(0, Math.min(1, value));
  
  // Convertir en pourcentage avec 2 décimales
  const percentage = (clampedValue * 100).toFixed(2);
  
  // Supprimer les zéros inutiles à la fin (ex: 25.00% -> 25%)
  const formattedPercentage = parseFloat(percentage).toString();
  
  return formattedPercentage + '%';
};

export function CardDetail() {
  const { t, lang, getPathForLang } = useTranslation();
  const { code, id } = useParams<{ code: string; id: string }>();
  const { data: card, loading, error } = useQuery<PokemonCard>('cards', { id: parseInt(id!) }, { single: true });
  const [setInfo, setSetInfo] = useState<SetInfo | null>(null);
  const [packInfo, setPackInfo] = useState<PackInfo | null>(null);
  const [packInfos, setPackInfos] = useState<PackInfo[]>([]);
  const [loadingSetInfo, setLoadingSetInfo] = useState(true);
  const [loadingPackInfo, setLoadingPackInfo] = useState(true);
  
  // Nouvel état pour stocker les cartes du set
  const [setCards, setSetCards] = useState<PokemonCard[]>([]);
  const [loadingSetCards, setLoadingSetCards] = useState(false);

  // État pour le préchargement des images
  const [setImageStatus, setSetImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [packImageStatus, setPackImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  const { data: tradeSettings } = useTradeSettings();

  // Fonction pour obtenir le nom de la carte en fonction de la langue sélectionnée
  const getCardName = (card: PokemonCard | null) => {
    if (!card) return '';
    return lang === 'en' ? (card.card_en || card.card_fr || '') : (card.card_fr || card.card_en || '');
  };

  // Fonction pour obtenir l'URL de l'image en fonction de la langue
  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return null;
    return `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${imagePath}`;
  };

  // Charger les informations du set et du pack
  useEffect(() => {
    const loadSetInfo = async () => {
      if (!card?.set) return;
      
      try {
        setLoadingSetInfo(true);
        const { data, error } = await supabase
          .from('sets')
          .select('*')
          .eq('name', card.set)
          .single();

        if (error) throw error;
        if (data) {
          setSetInfo(data);
          // Précharger l'image du set
          if (data.img) {
            const img = new Image();
            img.src = `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${data.img}`;
            img.onload = () => setSetImageStatus('loaded');
            img.onerror = () => setSetImageStatus('error');
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations du set:', error);
        setSetImageStatus('error');
      } finally {
        setLoadingSetInfo(false);
      }
    };

    const loadPackInfo = async () => {
      if (!card?.pack) {
        setPackInfo(null);
        setPackInfos([]);
        setLoadingPackInfo(false);
        return;
      }
      
      try {
        setLoadingPackInfo(true);

        // Si pack est "all" ou "All", charger tous les packs du set
        if (card.pack.toLowerCase() === 'all') {
          const { data, error } = await supabase
            .from('packs')
            .select('*')
            .eq('set', card.set);

          if (error) throw error;
          setPackInfos(data || []);
          setPackInfo(null);
        } else {
          // Sinon, charger le pack spécifique
          const { data, error } = await supabase
            .from('packs')
            .select('*')
            .eq('name', card.pack)
            .single();

          if (error) throw error;
          setPackInfo(data);
          setPackInfos([]);
          
          // Précharger l'image du pack
          if (data.img) {
            const img = new Image();
            img.src = `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${data.img}`;
            img.onload = () => setPackImageStatus('loaded');
            img.onerror = () => setPackImageStatus('error');
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations du pack:', error);
        setPackInfo(null);
        setPackInfos([]);
      } finally {
        setLoadingPackInfo(false);
      }
    };

    loadSetInfo();
    loadPackInfo();
  }, [card?.set, card?.pack]);

  // Charger les cartes du set
  useEffect(() => {
    const fetchSetCards = async () => {
      if (!card?.set) return;
      
      try {
        setLoadingSetCards(true);
        
        // Récupérer toutes les cartes du set
        const { data: allSetCards, error: setError } = await supabase
          .from('cards')
          .select('*')
          .eq('set', card.set);
          
        if (setError) throw setError;
        
        if (allSetCards) {
          // Si la carte est "all", nous récupérons toutes les cartes du set
          // mais nous les traiterons différemment dans le calcul des probabilités
          setSetCards(allSetCards);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des cartes du set:', error);
      } finally {
        setLoadingSetCards(false);
      }
    };
    
    fetchSetCards();
  }, [card?.set]);

  const { collection, updateCardQuantity, updateCardWanted, updateCardTradable } = useCollection();
  const { cards: similarCards, loading: loadingSimilar } = useSimilarCards(
    card?.card_fr || null,
    parseInt(id!)
  );
  const [availableTrades, setAvailableTrades] = useState<Trade[]>([]);
  const [loadingTrades, setLoadingTrades] = useState(true);

  useEffect(() => {
    const fetchAvailableTrades = async () => {
      if (!id) return;
      
      try {
        setLoadingTrades(true);
        const { data: trades, error } = await supabase
          .from('trades')
          .select(`
            id,
            status,
            created_at,
            user_id,
            creator:profiles!trades_user_id_fkey1(username),
            wanted_card:cards!trades_wanted_card_id_fkey(
              id,
              card_fr,
              card_en,
              image,
              set,
              rarity
            )
          `)
          .eq('status', 'pending');

        if (error) throw error;

        // Récupérer les cartes proposées pour chaque échange
        const tradesWithCards = await Promise.all(
          (trades || []).map(async (trade) => {
            const { data: tradeCards } = await supabase
              .from('trade_cards')
              .select(`
                card:cards!trade_cards_card_id_fkey(
                  id
                )
              `)
              .eq('trade_id', trade.id)
              .eq('card_id', parseInt(id));

            // Ne garder que les échanges qui proposent la carte actuelle
            return tradeCards && tradeCards.length > 0 ? trade : null;
          })
        );

        const validTrades = tradesWithCards
          .filter((trade): trade is Trade => 
            trade !== null && 
            trade.creator && 
            typeof trade.creator.username === 'string' &&
            trade.wanted_card
          );

        setAvailableTrades(validTrades);
      } catch (error) {
        console.error('Erreur lors de la récupération des échanges disponibles:', error);
      } finally {
        setLoadingTrades(false);
      }
    };

    fetchAvailableTrades();
  }, [id]);

  // Fonction pour vérifier si une carte est éligible à l'échange
  const isCardTradeable = (rarity: string, cardSet: string) => {
    if (!tradeSettings?.[0]) return false;
    
    const tradingRules = tradeSettings[0];
    return (
      tradingRules.eligible_sets.includes(cardSet) && 
      tradingRules.eligible_rarities.includes(rarity)
    );
  };

  // Fonction pour calculer la probabilité d'obtenir cette carte en fonction de sa position
  const calculateCardProbability = (rarity: string, position: 'first_three' | 'fourth' | 'fifth', packName?: string): number => {
    // Si la rareté n'est pas définie, retourner 0
    if (!rarity) return 0;
    
    // Convertir les symboles ☆ en ★ pour la correspondance avec les probabilités
    let normalizedRarity = rarity;
    if (rarity === '☆') normalizedRarity = '★';
    if (rarity === '☆☆') normalizedRarity = '★★';
    if (rarity === '☆☆☆') normalizedRarity = '★★★';
    
    // Obtenir les cartes pertinentes pour le calcul
    let relevantCards: PokemonCard[] = [];
    
    if (setCards.length > 0) {
      if (card?.pack?.toLowerCase() === 'all' && packName) {
        relevantCards = setCards.filter(c => 
          c.pack === packName || c.pack?.toLowerCase() === 'all'
        );
      } else if (card?.pack && card.pack.toLowerCase() !== 'all') {
        relevantCards = setCards.filter(c => 
          c.pack === card.pack || c.pack?.toLowerCase() === 'all'
        );
      } else {
        relevantCards = setCards;
      }
    }
    
    // Obtenir les probabilités appropriées en fonction de la présence de cartes shiny
    const probabilities = getPositionProbabilities(relevantCards);
    
    // Récupérer la probabilité pour cette rareté à cette position
    const rarityProbability = probabilities[position][normalizedRarity] || 0;
    
    // Si la probabilité est 0, inutile de continuer
    if (rarityProbability === 0) return 0;
    
    // Compter le nombre de cartes de cette rareté
    let totalCardsWithSameRarity = relevantCards.filter(c => c.rarity === rarity).length;
    
    // Si nous n'avons pas pu déterminer le nombre de cartes, utiliser une estimation
    if (totalCardsWithSameRarity === 0) {
      const estimatedCardsByRarity: Record<string, number> = {
        '♢': 30,     // Cartes communes
        '♢♢': 20,    // Cartes peu communes
        '♢♢♢': 10,   // Cartes rares
        '♢♢♢♢': 5,   // Cartes très rares
        '★': 8,      // Cartes holo rares
        '★★': 4,     // Cartes ultra rares
        '★★★': 2,    // Cartes secrètes rares
        '♛': 1,      // Cartes spéciales
        '☆': 8,      // Cartes holo rares (symbole alternatif)
        '☆☆': 4,     // Cartes ultra rares (symbole alternatif)
        '☆☆☆': 2,    // Cartes secrètes rares (symbole alternatif)
        '✧': 3,      // Cartes shiny (1 icône)
        '✧✧': 1,     // Cartes shiny (2 icônes)
      };
      
      totalCardsWithSameRarity = estimatedCardsByRarity[rarity] || 1;
    }
    
    // Calculer la probabilité d'obtenir cette carte spécifique
    return rarityProbability / Math.max(1, totalCardsWithSameRarity);
  };

  if (loading || loadingSetInfo) {
    return (
      <PageLayout
        breadcrumbProps={{
          setName: t('card_detail.loading'),
          cardName: t('card_detail.loading')
        }}
      >
        <CardDetailSkeleton />
      </PageLayout>
    );
  }

  if (error || !card) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{t('card_detail.error_loading')}</p>
      </div>
    );
  }

  const cardName = getCardName(card);
  const imageUrl = card.image ? getImageUrl(card.image) : null;
  const userCard = collection[card.id] || { quantity: 0, wanted: false, tradable: false };
  const quantity = userCard.quantity;
  const isCollected = quantity > 0;

  return (
    <PageLayout
      breadcrumbProps={{
        setName: setInfo?.display_name,
        cardName: cardName
      }}
    >
      {cardName && (
        <PageTitle title={cardName} />
      )}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-8">
          {/* Section Image */}
          {imageUrl && (
            <div className="relative min-h-[400px] sm:min-h-[500px] md:min-h-[600px] lg:min-h-0 lg:h-auto p-4 sm:p-6 md:p-8 flex items-center justify-center bg-gray-50">
              <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                {isCollected ? (
                  <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('card_detail.in_collection')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1.5 rounded-full">
                    <XCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('card_detail.not_collected')}</span>
                  </div>
                )}
              </div>

              {/* Boutons wanted/tradable - seulement si la carte est éligible */}
              {isCardTradeable(card.rarity, card.set) && (
                <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                  <button
                    onClick={() => updateCardWanted(card.id, !userCard.wanted)}
                    className={`p-2 rounded-full transition-colors shadow-lg backdrop-blur-sm ${
                      userCard.wanted
                        ? 'bg-red-100/90 text-red-600 hover:bg-red-200'
                        : 'bg-white/90 text-gray-600 hover:bg-gray-100'
                    }`}
                    title={userCard.wanted ? t('card_detail.remove_from_wishlist') : t('card_detail.add_to_wishlist')}
                  >
                    <Heart className="w-5 h-5" fill={userCard.wanted ? "currentColor" : "none"} />
                  </button>
                  
                  <button
                    onClick={() => updateCardTradable(card.id, !userCard.tradable)}
                    className={`p-2 rounded-full transition-colors shadow-lg backdrop-blur-sm ${
                      userCard.tradable
                        ? 'bg-green-100/90 text-green-600 hover:bg-green-200'
                        : 'bg-white/90 text-gray-600 hover:bg-gray-100'
                    }`}
                    title={userCard.tradable ? t('card_detail.remove_from_tradables') : t('card_detail.mark_as_tradable')}
                  >
                    <Repeat className="w-5 h-5" />
                  </button>
                </div>
              )}

              <img
                className={`max-w-full max-h-full object-contain ${!isCollected ? 'grayscale' : ''}`}
                src={imageUrl}
                alt={cardName || t('card_detail.pokemon_card')}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
            </div>
          )}

          {/* Section Détails */}
          <div className="p-4 sm:p-6 md:p-8 lg:p-10">
            <div className="space-y-6 sm:space-y-8">
              {/* En-tête */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  {cardName}
                </h1>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <span className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-800 rounded-full">
                    {card.rarity}
                  </span>
                  <span className="text-sm text-gray-500">
                    ID: {card.card_id}
                  </span>
                </div>
              </div>

              {/* Informations Principales */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <InfoCard
                  icon={<LayersIcon className="w-5 h-5" />}
                  label={t('card_detail.set')}
                  className="h-40"
                  value={
                    setInfo?.img && setImageStatus === 'loaded' ? (
                      <div className="flex flex-col items-center justify-center w-full h-[calc(100%-24px)] gap-2">
                        <div className="relative w-full h-20 flex items-center justify-center">
                          <img 
                            src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${setInfo.img}`} 
                            alt={setInfo.name || t('card_detail.set')} 
                            className="max-h-20 w-auto object-contain" 
                          />
                        </div>
                        <span className="text-sm text-gray-600">{lang === 'en' ? (setInfo.display_name_en || setInfo.display_name) : setInfo.display_name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full h-[calc(100%-24px)] gap-2">
                        {setImageStatus === 'loading' ? (
                          <div className="w-20 h-20 flex items-center justify-center">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-600">{lang === 'en' ? (setInfo?.display_name_en || setInfo?.display_name) : setInfo?.display_name || t('card_detail.not_specified')}</span>
                        )}
                      </div>
                    )
                  }
                />
                <InfoCard
                  icon={<Package className="w-5 h-5" />}
                  label={t('card_detail.pack')}
                  className="h-40"
                  value={
                    packInfo ? (
                      <div className="flex flex-col items-center justify-center w-full h-[calc(100%-24px)] gap-2">
                        <div className="relative w-full h-20 flex items-center justify-center">
                          <img 
                            src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${packInfo.img}`} 
                            alt={packInfo.name || t('card_detail.pack')} 
                            className="max-h-20 w-auto object-contain"
                          />
                        </div>
                        <span className="text-sm text-gray-600">{packInfo.display_name || packInfo.name}</span>
                      </div>
                    ) : packInfos.length > 0 ? (
                      <div className="h-full w-full">
                        <div className="grid grid-cols-2 gap-1.5 h-[120px] overflow-y-auto scrollbar-hide px-1">
                          {packInfos.map(pack => (
                            <div key={pack.name} className="flex flex-col items-center justify-center p-1.5 bg-white rounded-lg shadow-sm">
                              <div className="relative w-full h-10 flex items-center justify-center">
                                {pack.img && (
                                  <img
                                    src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${pack.img}`}
                                    alt={pack.display_name || pack.name}
                                    className="max-h-full max-w-full object-contain"
                                  />
                                )}
                              </div>
                              <p className="text-[10px] leading-tight font-medium text-gray-900 text-center mt-0.5 line-clamp-2 px-0.5">
                                {pack.display_name || pack.name}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        {t('card_detail.no_pack_available')}
                      </div>
                    )
                  }
                />
              </div>

              {/* Type et Énergie */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-500" />
                  {t('card_detail.characteristics')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-0.5">{t('card_detail.type')}</div>
                        <div className="font-medium text-gray-900">
                          {card.type || t('card_detail.not_specified')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {card.type === 'Pokémon' && (
                    <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Zap className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-0.5">{t('card_detail.energy')}</div>
                          <div className="font-medium text-gray-900">
                            {card.energy || t('card_detail.not_specified')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Annonce AdSense #1 - Après les informations principales */}
              <div className="mt-6 mb-8">
                <AdSense adSlot="1424794127" />
              </div>

              {/* Statistiques de Distribution */}
              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-6 border border-gray-100 shadow-sm mt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-500" />
                  {t('card_detail.pull_probabilities')}
                </h2>
                
                <div className="space-y-4">
                  {/* Explication */}
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-sm text-blue-800">
                    <div className="flex items-start gap-2">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p>
                        {card?.pack?.toLowerCase() === 'all' ? (
                          <>
                            {t('card_detail.all_packs_explanation')}
                          </>
                        ) : (
                          <>
                            {t('card_detail.probability_explanation')}{' '}
                            <strong>{t('card_detail.specific_card')}</strong>{' '}
                            {t('card_detail.based_on_position')}
                            {t('card_detail.calculations_explanation')}{' '}
                            {card?.pack ? t('card_detail.in_specific_pack', { pack: card.pack }) : t('card_detail.in_this_pack')}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Grille de probabilités - Affichée uniquement si la carte n'est pas "all" */}
                  {card?.pack?.toLowerCase() !== 'all' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 rounded-full font-bold text-xs">1-3</span>
                            <span className="text-sm font-medium text-gray-700">{t('card_detail.positions_1_3')}</span>
                          </div>
                          <Percent className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div className="text-xl font-semibold text-indigo-600">
                          {formatPercent(calculateCardProbability(card?.rarity || '', 'first_three', card?.pack))}
                        </div>
                        {loadingSetCards && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            {t('card_detail.calculating')}
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 rounded-full font-bold text-xs">4</span>
                            <span className="text-sm font-medium text-gray-700">{t('card_detail.position_4')}</span>
                          </div>
                          <Percent className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div className="text-xl font-semibold text-indigo-600">
                          {formatPercent(calculateCardProbability(card?.rarity || '', 'fourth', card?.pack))}
                        </div>
                        {loadingSetCards && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            {t('card_detail.calculating')}
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 rounded-full font-bold text-xs">5</span>
                            <span className="text-sm font-medium text-gray-700">{t('card_detail.position_5')}</span>
                          </div>
                          <Percent className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div className="text-xl font-semibold text-indigo-600">
                          {formatPercent(calculateCardProbability(card?.rarity || '', 'fifth', card?.pack))}
                        </div>
                        {loadingSetCards && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            {t('card_detail.calculating')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Affichage des probabilités pour chaque booster si la carte est "All" */}
                  {card?.pack?.toLowerCase() === 'all' && packInfos.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">{t('card_detail.probabilities_by_booster')}:</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {packInfos.map(pack => (
                          <div key={pack.name} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 flex-shrink-0 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden">
                                {pack.img && (
                                  <img
                                    src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${pack.img}`}
                                    alt={pack.display_name || pack.name}
                                    className="max-h-full max-w-full object-contain"
                                  />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {pack.display_name || pack.name}
                                </div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              <div className="bg-gray-50 p-2 rounded flex flex-col items-center">
                                <div className="text-xs text-gray-500 mb-1">{t('card_detail.pos_1_3_short')}</div>
                                <div className="text-sm font-semibold text-indigo-600">
                                  {formatPercent(calculateCardProbability(card?.rarity || '', 'first_three', pack.name))}
                                </div>
                              </div>
                              <div className="bg-gray-50 p-2 rounded flex flex-col items-center">
                                <div className="text-xs text-gray-500 mb-1">{t('card_detail.pos_4_short')}</div>
                                <div className="text-sm font-semibold text-indigo-600">
                                  {formatPercent(calculateCardProbability(card?.rarity || '', 'fourth', pack.name))}
                                </div>
                              </div>
                              <div className="bg-gray-50 p-2 rounded flex flex-col items-center">
                                <div className="text-xs text-gray-500 mb-1">{t('card_detail.pos_5_short')}</div>
                                <div className="text-sm font-semibold text-indigo-600">
                                  {formatPercent(calculateCardProbability(card?.rarity || '', 'fifth', pack.name))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Compteur Collection */}
              <div className="pt-4 border-t border-gray-200 mt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <span className="text-lg font-medium text-gray-900">
                    {t('card_detail.in_your_collection')}
                  </span>
                  <div className="flex items-center gap-4 sm:gap-6">
                    <button
                      onClick={() => updateCardQuantity(card.id, Math.max(0, quantity - 1))}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={quantity}
                      onChange={(e) => {
                        const newValue = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                        if (!isNaN(newValue)) {
                          updateCardQuantity(card.id, Math.max(0, newValue));
                        }
                      }}
                      className="w-16 text-center text-xl font-semibold border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={() => updateCardQuantity(card.id, quantity + 1)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Annonce AdSense #3 - Entre les cartes similaires et les échanges disponibles */}
      <div className="mt-6 mb-8">
        <AdSense adSlot="1424794127" />
      </div>

      {/* Section des cartes similaires */}
      <div className="mt-8">
        <div className="bg-white rounded-2xl shadow-md p-8 border border-indigo-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('card_detail.similar_cards')}</h2>
            <div className="text-sm text-gray-500">
              {similarCards.length} {t('card_detail.card', { count: similarCards.length })} {t('card_detail.found', { count: similarCards.length })}
            </div>
          </div>

          {loadingSimilar ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : similarCards.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {similarCards.map(card => {
                const userCard = collection[card.id] || { quantity: 0, wanted: false, tradable: false };
                const isCollected = userCard.quantity > 0;
                const isEligible = isCardTradeable(card.rarity, card.set);
                const similarCardName = lang === 'en' ? (card.card_en || card.card_fr) : (card.card_fr || card.card_en);

                // Construction du chemin avec prise en compte de la langue
                const cardPath = getPathForLang(`/sets/${card.set}/cards/${card.id}`, lang);

                return (
                  <Link
                    key={card.id}
                    to={cardPath}
                    className="group relative bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-indigo-200"
                  >
                    <div className="aspect-[3/4] p-2">
                      {card.image && (
                        <img
                          src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${card.image}`}
                          alt={similarCardName}
                          className={`w-full h-full object-contain rounded-lg transition-all duration-300 group-hover:scale-110 ${
                            !isCollected ? 'grayscale' : ''
                          }`}
                        />
                      )}

                      {/* Badge de quantité */}
                      {userCard.quantity > 0 && (
                        <div className="absolute top-2 right-2">
                          <div className="bg-indigo-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                            {userCard.quantity}
                          </div>
                        </div>
                      )}
                      
                      {/* Boutons wanted/tradable - seulement si la carte est éligible */}
                      {isEligible && (
                        <div className="absolute bottom-[25%] left-0 right-0 flex justify-center gap-2 transition-transform duration-200 transform hover:scale-105"
                          style={{ zIndex: 20 }}
                        >
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              updateCardWanted(card.id, !userCard.wanted);
                            }}
                            className={`p-2 rounded-full transition-colors shadow-lg backdrop-blur-sm ${
                              userCard.wanted
                                ? 'bg-red-100/90 text-red-600 hover:bg-red-200'
                                : 'bg-white/90 text-gray-600 hover:bg-gray-100'
                            }`}
                            title={userCard.wanted ? t('card_detail.remove_from_wishlist') : t('card_detail.add_to_wishlist')}
                          >
                            <Heart className="w-4 h-4" fill={userCard.wanted ? "currentColor" : "none"} />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              updateCardTradable(card.id, !userCard.tradable);
                            }}
                            className={`p-2 rounded-full transition-colors shadow-lg backdrop-blur-sm ${
                              userCard.tradable
                                ? 'bg-green-100/90 text-green-600 hover:bg-green-200'
                                : 'bg-white/90 text-gray-600 hover:bg-gray-100'
                            }`}
                            title={userCard.tradable ? t('card_detail.remove_from_tradables') : t('card_detail.mark_as_tradable')}
                          >
                            <Repeat className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="p-3 bg-white border-t border-gray-100">
                      <h3 className="text-sm font-medium text-gray-900 truncate mb-1">
                        {similarCardName}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full truncate">
                          {card.set}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full truncate">
                          {card.rarity}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-500">
                {t('card_detail.no_similar_cards')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Section des échanges disponibles */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-6">{t('card_detail.available_trades')}</h2>
        
        {loadingTrades ? (
          <div className="flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : availableTrades.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableTrades.map((trade) => {
              const tradeCardName = lang === 'en' ? (trade.wanted_card.card_en || trade.wanted_card.card_fr) : trade.wanted_card.card_fr;
              
              return (
              <TradeCard key={trade.id}>
                <TradeCardImage>
                  <img
                    src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${trade.wanted_card.image}`}
                    alt={tradeCardName}
                    className={
                      collection[trade.wanted_card.id]?.quantity > 0
                        ? ''
                        : 'not-collected'
                    }
                  />
                </TradeCardImage>
                
                <TradeCardContent>
                  <TradeCardHeader>
                    <UserInfo>
                      <div className="avatar">
                        <span className="text-gray-500 text-sm">
                          {trade.creator.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium text-gray-900">
                        {trade.creator.username}
                      </span>
                    </UserInfo>
                    <span className="text-sm text-gray-500">
                      {new Date(trade.created_at).toLocaleDateString()}
                    </span>
                  </TradeCardHeader>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-gray-900">
                      {tradeCardName}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                        {trade.wanted_card.set}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                        {trade.wanted_card.rarity}
                      </span>
                    </div>
                  </div>
                  
                  <Link
                    to={getPathForLang(`/trades/${trade.id}/info`, lang)}
                    className="mt-4 block w-full text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    {t('card_detail.view_trade')}
                  </Link>
                </TradeCardContent>
              </TradeCard>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-500">
              {t('card_detail.no_trades_available')}
            </p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

interface InfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  className?: string;
}

function InfoCard({ icon, label, value, className = '' }: InfoCardProps) {
  const { t } = useTranslation();
  return (
    <div className={`bg-gray-50 rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-2 text-gray-500 mb-1">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <div className="text-gray-900 font-medium truncate">
        {value || t('card_detail.not_specified')}
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number | null;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="text-center">
      <div className="text-xl sm:text-2xl font-semibold text-indigo-600">
        {value ?? '0'}
      </div>
      <div className="text-xs sm:text-sm text-gray-500">{label}</div>
    </div>
  );
}